import { ThemeEvents } from '@theme/events';

/**
 * Quantity control for the mobile sticky purchase bar (snippets/bekra-sticky-quantity.liquid).
 *
 * Holds no quantity of its own: -/+ call updateQuantity() on the PDP product form's
 * real <quantity-selector-component> (visually hidden in the Buy Box), so min/max/step
 * rules, the QuantitySelectorUpdateEvent and the submitted form quantity all stay
 * Horizon's. The sticky "In den Warenkorb" button already clicks that same form's
 * add-to-cart button, so the cart receives exactly the quantity shown here.
 */
class BekraStickyQuantity extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#sync, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#syncSoon, { signal });
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#syncSoon, { signal });

    this.#sync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {any} the Buy Box quantity-selector-component of this product */
  #getMainSelector() {
    const productId = this.closest('sticky-add-to-cart')?.getAttribute('data-product-id');
    const section = this.closest('.shopify-section');
    if (!productId || !section) return null;
    return section.querySelector(
      `product-form-component[data-product-id="${productId}"] quantity-selector-component`
    );
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-bekra-qty-step]') : null
    );
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const main = this.#getMainSelector();
    if (typeof main?.updateQuantity !== 'function') return;

    main.updateQuantity(Number(button.dataset.bekraQtyStep) || 0);
    this.#sync();
  };

  #syncSoon = () => {
    // Variant updates morph both the product form and the sticky bar; read after they settle.
    requestAnimationFrame(() => requestAnimationFrame(this.#sync));
  };

  #sync = () => {
    const main = this.#getMainSelector();
    const input = main?.querySelector('input[name="quantity"]');
    const valueEl = this.querySelector('[data-bekra-qty-value]');
    const [minus, plus] = this.querySelectorAll('[data-bekra-qty-step]');

    if (!(input instanceof HTMLInputElement) || !valueEl || !minus || !plus) {
      // Without the real form selector this control could not change anything.
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const min = parseInt(input.min) || 1;
    const value = parseInt(input.value) || min;
    const max = typeof main.getEffectiveMax === 'function' ? main.getEffectiveMax() : null;
    const unavailable = input.disabled;

    valueEl.textContent = String(value);
    /** @type {HTMLButtonElement} */ (minus).disabled = unavailable || value <= min;
    /** @type {HTMLButtonElement} */ (plus).disabled = unavailable || (max !== null && value >= max);
  };
}

if (!customElements.get('bekra-sticky-quantity')) {
  customElements.define('bekra-sticky-quantity', BekraStickyQuantity);
}
